import React, { useEffect, useContext } from 'react';
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { Card, CardActionArea, CardContent, Grid, Typography } from '@material-ui/core';
import { Container } from '../../../../Components';
import { ModuleContext } from '../../../../Context/ModuleContext/ModuleContext';
import { AccessLevel, LawPortalFeature, Module, UserContext } from '../../../../Context/UserContext/UserContext';
import { Business, FormatListBulleted, Gavel, LiveHelp } from '@material-ui/icons';
import { Organization } from '../../../Administration/Features/Organization';
import { CustomerLaws } from '../../../Administration/Features/LawPortal/CustomerLaws/CustomerLaws.List';
import { AddCustomerLaws } from '../../../Administration/Features/LawPortal/CustomerLaws/CustomerLaws.Add';
import { EditCustomerLaw } from '../../../Administration/Features/LawPortal/CustomerLaws/CustomerLaw.Edit';
import { UpdateCustomerLaws } from '../../../Administration/Features/LawPortal/CustomerLaws/CustomerLaws.Update';
import {
  AddLawCompanyUnit,
  EditLawCompanyUnit,
} from '../../../Administration/Features/LawPortal/CustomerLaws/CompanyUnits/LawCompanyUnits.Edit';
import { EditOrganizationLawPortalChecklist } from '../../../Administration/Features/LawPortal/CustomerLaws/CompanyUnits/Checklist/Checklist.Edit';
import { OtherRequirements as ManagedOtherRequirements } from '../../../Administration/Features/LawPortal/OtherRequirements/CustomerRequirements.List';
import { NewRequirement } from '../../../Administration/Features/LawPortal/OtherRequirements/CustomerRequirement.Create';
import { EditRequirement } from '../../../Administration/Features/LawPortal/OtherRequirements/CustomerRequirement.Edit';
import {
  AddRequirementCompanyUnit,
  EditRequirementCompanyUnit,
} from '../../../Administration/Features/LawPortal/OtherRequirements/CompanyUnits/RequirementCompanyUnits.Edit';
import { EditRequirementChecklist } from '../../../Administration/Features/LawPortal/OtherRequirements/CompanyUnits/Checklist';
import { Help } from '../Help';
import { useStyles } from './Settings.styles';

export const Settings = () => {
  const { setHeader } = useContext(ModuleContext);
  const { hasAccessToFeature, hasAccessToModule } = useContext(UserContext);
  const { formatMessage } = useIntl();
  const location = useLocation();
  const classes = useStyles();
  const accessToOurLaws = hasAccessToFeature(LawPortalFeature.OurLaws, AccessLevel.Read);
  const accessToOtherRequirements = hasAccessToFeature(LawPortalFeature.OtherRequirements, AccessLevel.Read);
  const canManageLawPortal = hasAccessToFeature(LawPortalFeature.Manage, AccessLevel.Manage);

  useEffect(() => {
    const section = location.pathname.split('/').filter(Boolean)[2];
    const headerBySection: Record<string, string> = {
      organization: formatMessage({ id: 'organization.header', defaultMessage: 'Organization' }),
      'legal-list': formatMessage({ id: 'law-portal.manage.law-list', defaultMessage: 'Legal list' }),
      'other-requirements': formatMessage({
        id: 'law-portal.manage.requirements',
        defaultMessage: 'Other requirements',
      }),
      help: formatMessage({ id: 'law-portal.help', defaultMessage: 'Help' }),
    };

    setHeader(headerBySection[section] || formatMessage({ id: 'settings.header', defaultMessage: 'Settings' }));
  }, [location.pathname, setHeader, formatMessage]);

  const settingsHome = (
    <Container>
      <Grid container spacing={3} className={classes.grid}>
        {hasAccessToModule(Module.Organization) && (
          <SettingsCard
            to="organization"
            title={formatMessage({ id: 'organization.header', defaultMessage: 'Organization' })}
            description={formatMessage({
              id: 'settings.card.organization.description',
              defaultMessage: 'Manage organization settings and users.',
            })}
            icon={<Business />}
          />
        )}
        {canManageLawPortal && hasAccessToModule(Module.LawPortal) && accessToOurLaws && (
          <SettingsCard
            to="legal-list"
            title={formatMessage({ id: 'law-portal.manage.law-list', defaultMessage: 'Legal list' })}
            description={formatMessage({
              id: 'settings.card.legal-list.description',
              defaultMessage: 'Manage connected laws and legal list settings.',
            })}
            icon={<Gavel />}
          />
        )}
        {canManageLawPortal && hasAccessToModule(Module.LawPortal) && accessToOtherRequirements && (
          <SettingsCard
            to="other-requirements"
            title={formatMessage({ id: 'law-portal.manage.requirements', defaultMessage: 'Other requirements' })}
            description={formatMessage({
              id: 'settings.card.other-requirements.description',
              defaultMessage: 'Manage other requirements and their connections.',
            })}
            icon={<FormatListBulleted />}
          />
        )}
        <SettingsCard
          to="help"
          title={formatMessage({ id: 'law-portal.help', defaultMessage: 'Help' })}
          description={formatMessage({
            id: 'settings.card.help.description',
            defaultMessage: 'Open help and support settings.',
          })}
          icon={<LiveHelp />}
        />
      </Grid>
    </Container>
  );

  return (
    <Routes>
      <Route path="/" element={settingsHome} />
      {hasAccessToModule(Module.Organization) && <Route path="organization/*" element={<Organization suppressHeader />} />}
      {canManageLawPortal && hasAccessToModule(Module.LawPortal) && accessToOurLaws && (
        <>
          <Route path="legal-list" element={<CustomerLaws suppressHeader />} />
          <Route path="legal-list/new" element={<AddCustomerLaws />} />
          <Route path="legal-list/update-for-company-unit/:unitid" element={<UpdateCustomerLaws />} />
          <Route path="legal-list/:lawid/company-units/new" element={<AddLawCompanyUnit />} />
          <Route
            path="legal-list/:lawid/company-units/:unitid/checklist"
            element={<EditOrganizationLawPortalChecklist />}
          />
          <Route path="legal-list/:lawid/company-units/:unitid" element={<EditLawCompanyUnit />} />
          <Route path="legal-list/:lawid" element={<EditCustomerLaw />} />
        </>
      )}
      {canManageLawPortal && hasAccessToModule(Module.LawPortal) && accessToOtherRequirements && (
        <>
          <Route path="other-requirements" element={<ManagedOtherRequirements suppressHeader />} />
          <Route path="other-requirements/new" element={<NewRequirement />} />
          <Route path="other-requirements/:reqid/company-units/new" element={<AddRequirementCompanyUnit />} />
          <Route
            path="other-requirements/:reqid/company-units/:unitid/checklist"
            element={<EditRequirementChecklist />}
          />
          <Route path="other-requirements/:reqid/company-units/:unitid" element={<EditRequirementCompanyUnit />} />
          <Route path="other-requirements/:reqid" element={<EditRequirement />} />
        </>
      )}
      <Route path="help" element={<Help suppressHeader />} />
      <Route path="*" element={<Navigate to="." replace />} />
    </Routes>
  );
};

const SettingsCard = (props: { to: string; title: string; description: string; icon: React.ReactNode }) => {
  const classes = useStyles();

  return (
    <Grid item xs={12} md={6}>
      <Card className={classes.card}>
        <CardActionArea component={Link} to={props.to} className={classes.cardAction}>
          <div className={classes.icon}>{props.icon}</div>
          <CardContent className={classes.cardContent}>
            <Typography variant="h4" component="h2" className={classes.title}>
              {props.title}
            </Typography>
            <Typography variant="body1" color="textSecondary" className={classes.description}>
              {props.description}
            </Typography>
          </CardContent>
        </CardActionArea>
      </Card>
    </Grid>
  );
};